import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { adapterServiceApi } from '../infrastructure/api/adapterServiceApi';
import { normalizeConfigLabel } from '../lib/adapterCapabilityCatalog';
import type { AdapterServiceDto } from '../domain/adapterService';

export const ADAPTER_SERVICES_QUERY_KEY = ['adapter-services'] as const;
export const ADAPTER_SERVICES_CATALOG_KEY = ['adapter-services', 'catalog'] as const;
export const ADAPTER_CONFIG_LABELS_KEY = ['adapter-services', 'config-labels'] as const;
export const ADAPTER_CONFIG_IMPORT_SOURCES_KEY = ['adapter-services', 'config-import-sources'] as const;
export const ADAPTER_SERVICE_KEYS_KEY = ['adapter-services', 'service-keys'] as const;

function configLabelsFromServices(services: AdapterServiceDto[] | undefined): string[] {
  const merged = new Set<string>(['default']);
  for (const svc of services ?? []) {
    merged.add(normalizeConfigLabel(svc.configLabel ?? 'default'));
  }
  return [...merged].sort((a, b) => a.localeCompare(b));
}

export function useAdapterServices() {
  return useQuery({
    queryKey: ADAPTER_SERVICES_QUERY_KEY,
    queryFn: () => adapterServiceApi.list(),
  });
}

/** Labels from service onboarding definitions (matches dashboard cards, not orphaned adapter_configs). */
export function useAdapterConfigLabels() {
  const servicesQuery = useAdapterServices();
  const data = useMemo(
    () => configLabelsFromServices(servicesQuery.data),
    [servicesQuery.data],
  );
  return {
    data,
    isPending: servicesQuery.isPending,
    isLoading: servicesQuery.isPending,
    isError: servicesQuery.isError,
    error: servicesQuery.error,
  };
}

export function useAdapterConfigImportSources() {
  return useQuery({
    queryKey: ADAPTER_CONFIG_IMPORT_SOURCES_KEY,
    queryFn: () => adapterServiceApi.getConfigImportSources(),
    staleTime: 60_000,
  });
}

export function useDeleteAdapterService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adapterServiceApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADAPTER_SERVICES_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ADAPTER_SERVICES_CATALOG_KEY });
      void qc.invalidateQueries({ queryKey: ADAPTER_CONFIG_LABELS_KEY });
    },
  });
}

export function usePublishPreview(serviceId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...ADAPTER_SERVICES_QUERY_KEY, serviceId, 'publish-preview'],
    queryFn: () => adapterServiceApi.getPublishPreview(serviceId!),
    enabled: Boolean(serviceId) && enabled,
  });
}

export function useAdapterServiceKeys() {
  return useQuery({
    queryKey: ADAPTER_SERVICE_KEYS_KEY,
    queryFn: () => adapterServiceApi.getServiceKeys(),
    staleTime: 60_000,
  });
}

export function useCapabilityTemplates(serviceId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...ADAPTER_SERVICES_QUERY_KEY, serviceId, 'capability-templates'],
    queryFn: () => adapterServiceApi.getCapabilityTemplates(serviceId!),
    enabled: Boolean(serviceId) && enabled,
  });
}

export function useAdapterServiceCatalog(options?: { publishedOnly?: boolean }) {
  const publishedOnly = options?.publishedOnly !== false;
  return useQuery({
    queryKey: [...ADAPTER_SERVICES_CATALOG_KEY, publishedOnly],
    queryFn: () => adapterServiceApi.catalog({ publishedOnly }),
    staleTime: 60_000,
  });
}

export function useAdapterService(id: string | undefined) {
  return useQuery({
    queryKey: [...ADAPTER_SERVICES_QUERY_KEY, id],
    queryFn: () => adapterServiceApi.get(id!),
    enabled: Boolean(id),
  });
}

export function useBootstrapAdapterServices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adapterServiceApi.bootstrap(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADAPTER_SERVICES_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ADAPTER_SERVICES_CATALOG_KEY });
    },
  });
}

export function usePublishAdapterService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adapterServiceApi.publish(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADAPTER_SERVICES_QUERY_KEY });
    },
  });
}

export function useSaveAdapterCapability(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adapterServiceApi.upsertCapability.bind(null, serviceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...ADAPTER_SERVICES_QUERY_KEY, serviceId] });
      void qc.invalidateQueries({ queryKey: ADAPTER_SERVICES_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ADAPTER_SERVICES_CATALOG_KEY });
    },
  });
}

export function usePatchAdapterService(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof adapterServiceApi.patch>[1]) =>
      adapterServiceApi.patch(serviceId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...ADAPTER_SERVICES_QUERY_KEY, serviceId] });
      void qc.invalidateQueries({ queryKey: [...ADAPTER_SERVICES_QUERY_KEY, serviceId, 'publish-preview'] });
      void qc.invalidateQueries({ queryKey: ADAPTER_SERVICES_QUERY_KEY });
    },
  });
}

export function useDeleteAdapterCapability(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (capabilityId: string) => adapterServiceApi.removeCapability(serviceId, capabilityId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...ADAPTER_SERVICES_QUERY_KEY, serviceId] });
      void qc.invalidateQueries({ queryKey: ADAPTER_SERVICES_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ADAPTER_SERVICES_CATALOG_KEY });
    },
  });
}
